/**
 * <s-product-gallery> — PDP featured-media gallery with thumbnails.
 *
 * One large media slide is visible at a time inside a scroll-snap track.
 * Thumbnail buttons use commandfor/command="--show-media" (data-index) to
 * scroll the track; swiping the track updates the active thumbnail.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontal scroll-snap track.
 *   [data-gallery-slide]          — one per media item inside the track.
 *   button[data-gallery-thumb]    — thumbnail with data-index, commandfor pointing to this element.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #activeIndex = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('command', (event) => {
      if (event.command !== '--show-media') return;
      const index = parseInt(event.source?.dataset.index, 10);
      if (Number.isNaN(index)) return;
      this.#showMedia(index);
    }, opts);

    const track = this.#track;
    if (!track) return;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const index = this.#slides.indexOf(entry.target);
        if (index !== -1) this.#setActive(index);
      }
    }, { root: track, threshold: 0.6 });

    for (const slide of this.#slides) {
      this.#observer.observe(slide);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  get #track() {
    return this.querySelector('[data-gallery-track]');
  }

  get #slides() {
    return Array.from(this.querySelectorAll('[data-gallery-slide]'));
  }

  get #thumbs() {
    return Array.from(this.querySelectorAll('[data-gallery-thumb]'));
  }

  #showMedia(index) {
    const track = this.#track;
    const slide = this.#slides[index];
    if (!track || !slide) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
    this.#setActive(index);
  }

  // Scrolls only the thumbnail strip, never the page.
  #revealThumb(thumb) {
    const list = thumb.closest('[data-gallery-thumbs]');
    if (!list) return;
    const listRect = list.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    if (thumbRect.left < listRect.left) list.scrollLeft -= listRect.left - thumbRect.left;
    else if (thumbRect.right > listRect.right) list.scrollLeft += thumbRect.right - listRect.right;
    if (thumbRect.top < listRect.top) list.scrollTop -= listRect.top - thumbRect.top;
    else if (thumbRect.bottom > listRect.bottom) list.scrollTop += thumbRect.bottom - listRect.bottom;
  }

  #setActive(index) {
    if (index === this.#activeIndex && this.#thumbs[index]?.hasAttribute('aria-current')) return;
    this.#activeIndex = index;

    this.#thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
        this.#revealThumb(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    this.#slides.forEach((slide, i) => {
      if (i === index) return;
      for (const video of slide.querySelectorAll('video')) {
        if (!video.paused) video.pause();
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
